
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { supabase, codeTypes } from '../../lib/supabase';
import { categories as defaultCategories } from '../../mocks/categories';
import { incrementCardsCreatedCount } from '../../lib/admin';
import { ThemeToggle } from '../../components/ui/theme-toggle';
import { uploadThumbnailToSupabase, isExternalUrl } from '../../lib/imageUpload';
import { Button } from '../../components/ui/button';
import { IconButton } from '../../components/ui/icon-button';
import { GradientText } from '../../components/ui/gradient-text';
import { CodeAccordion } from '../../components/ui/code-accordion';
import { motion } from 'motion/react';

export default function CreateCard() {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    code_type: 'json',
    category: '',
    categories: [] as string[], // Adicionado campo categories
    code: '',
    // Novos campos para HTML/CSS/JS separados
    html_code: '',
    css_code: '',
    js_code: '',
    html_instructions: '',
    css_instructions: '',
    js_instructions: '',
    thumbnail: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploadingThumbnail, setIsUploadingThumbnail] = useState(false);
  const submittingRef = useRef(false); // Ref para evitar múltiplos submits simultâneos
  const [showNewCategoryModal, setShowNewCategoryModal] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [availableCategories, setAvailableCategories] = useState<string[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [thumbnailError, setThumbnailError] = useState<string | null>(null);
  const [thumbnailLoading, setThumbnailLoading] = useState(false);
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login', { replace: true });
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    // Carregar categorias: usar lista padrão do mock como base e adicionar categorias do banco
    // OTIMIZADO: carrega apenas campos de categoria, não todos os dados
    const loadCategories = async () => {
      try {
        // Começar com a lista padrão do mock
        const categorySet = new Set<string>(defaultCategories);
        
        // Buscar apenas campos de categoria do banco de dados (sem o campo data pesado)
        const { data, error } = await supabase
          .from('cards')
          .select('categories, category');
        
        if (!error && data) {
          data.forEach(card => {
            // Adicionar categorias do campo categories (array)
            if (card.categories && Array.isArray(card.categories)) {
              card.categories.forEach(cat => {
                if (cat && cat.trim()) {
                  categorySet.add(cat.trim());
                }
              });
            }
            // Adicionar categoria do campo category (string)
            if (card.category && card.category.trim()) {
              categorySet.add(card.category.trim());
            }
          });
        }
        
        // Converter para array e ordenar
        const categoriesArray = Array.from(categorySet).sort();
        setAvailableCategories(categoriesArray);
      } catch (error) {
        console.error('Erro ao carregar categorias:', error);
        // Em caso de erro, usar apenas a lista padrão
        setAvailableCategories([...defaultCategories].sort());
      } finally {
        setLoadingCategories(false);
      }
    };

    // REMOVIDO: loadExistingCards - não carrega mais todos os cards na página
    // A verificação de duplicados será feita apenas no momento do submit usando queries específicas
    // Isso reduz drasticamente o egress do Supabase

    if (user) {
      loadCategories();
    }
  }, [user]);

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center bg-white dark:bg-gradient-to-br dark:from-[#1a0f2b] dark:to-[#2d1b3d] p-8 rounded-2xl shadow-lg">
          <i className="ri-loader-4-line text-6xl text-[#6a0dad] animate-spin"></i>
          <p className="mt-4 text-gray-900 dark:text-[#e0e1dd] text-lg font-medium">Verificando autenticação...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  const handleAddCategory = async () => {
    if (newCategoryName.trim() && !availableCategories.includes(newCategoryName.trim())) {
      const newCategory = newCategoryName.trim();
      
      // Adicionar à lista local imediatamente
      setAvailableCategories(prev => [...prev, newCategory].sort());
      
      // Adicionar ao formulário
      setFormData({
        ...formData, 
        categories: [...formData.categories, newCategory],
        category: [...formData.categories, newCategory].join(',')
      });
      
      setNewCategoryName('');
      setShowNewCategoryModal(false);
    }
  };

  // OTIMIZADO: Verificação de duplicados usando funções RPC do Supabase
  // As funções RPC executam no servidor, sem transferir dados para o frontend
  // Isso reduz o egress do Supabase a quase zero
  const validateDuplicates = async (): Promise<boolean> => {
    if (!user) return false;

    try {
      // Verificar título duplicado usando função RPC (sem transferir dados)
      const { data: titleExists, error: titleError } = await supabase.rpc('check_duplicate_title', {
        p_user_id: user.id,
        p_title: formData.title.trim()
      });

      if (titleError) {
        console.error('Erro ao verificar título duplicado:', titleError);
        // Se a função RPC não existir, fazer fallback para query tradicional
        const { data: titleCheck, error: fallbackError } = await supabase
          .from('cards')
          .select('id')
          .eq('user_id', user.id)
          .ilike('title', formData.title.trim())
          .limit(1);

        if (!fallbackError && titleCheck && titleCheck.length > 0) {
          alert('Já existe um elemento com este título. Por favor, escolha um título diferente.');
          return false;
        }
        // Em caso de erro, permitir continuar (não bloquear por erro de rede)
        return true;
      }

      if (titleExists) {
        alert('Já existe um elemento com este título. Por favor, escolha um título diferente.');
        return false;
      }

      // Verificar código duplicado usando função RPC (sem transferir dados)
      const { data: codeExists, error: codeError } = await supabase.rpc('check_duplicate_code', {
        p_user_id: user.id,
        p_code: formData.code.trim()
      });

      if (codeError) {
        console.error('Erro ao verificar código duplicado:', codeError);
        // Se a função RPC não existir, fazer fallback para query tradicional
        // (ainda carrega data, mas é melhor que nada)
        const { data: codeCheck, error: fallbackError } = await supabase
          .from('cards')
          .select('data')
          .eq('user_id', user.id);

        if (!fallbackError && codeCheck && codeCheck.length > 0) {
          const exists = codeCheck.some(card => 
            card.data?.code === formData.code.trim()
          );

          if (exists) {
            alert('Já existe um elemento com este código. Por favor, insira um código diferente.');
            return false;
          }
        }
        // Em caso de erro, permitir continuar (não bloquear por erro de rede)
        return true;
      }

      if (codeExists) {
        alert('Já existe um elemento com este código. Por favor, insira um código diferente.');
        return false;
      }

      return true;
    } catch (error) {
      console.error('Erro ao validar duplicados:', error);
      // Em caso de erro, permitir continuar (não bloquear por erro de rede)
      return true;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Proteção contra múltiplos submits simultâneos
    if (submittingRef.current || isSubmitting) {
      console.log('⏸️ Submit já em andamento, ignorando clique duplicado');
      return;
    }
    
    if (!user) {
      alert('Você precisa estar logado para criar um elemento.');
      return;
    }

    // Validação diferente para JSON vs outros tipos
    if (formData.code_type === 'json') {
      if (!formData.title.trim() || !formData.code.trim() || !formData.thumbnail.trim()) {
        alert('Por favor, preencha pelo menos o título, código JSON e URL da miniatura.');
        return;
      }
    } else {
      // Para HTML/CSS/JS, pelo menos um dos códigos deve estar preenchido
      const hasAnyCode = formData.html_code.trim() || formData.css_code.trim() || formData.js_code.trim();
      if (!formData.title.trim() || !hasAnyCode || !formData.thumbnail.trim()) {
        alert('Por favor, preencha pelo menos o título, um código (HTML, CSS ou JS) e URL da miniatura.');
        return;
      }
    }

    // Marcar como submetendo IMEDIATAMENTE para evitar múltiplos cliques
    submittingRef.current = true;
    setIsSubmitting(true);

    let finalThumbnailUrl = formData.thumbnail.trim();
    
    // Verificar se é um blob URL - não permitir salvar blob URLs
    if (finalThumbnailUrl.startsWith('blob:')) {
      alert('A imagem ainda está sendo processada. Aguarde alguns segundos e tente novamente, ou faça upload novamente da imagem.');
      submittingRef.current = false;
      setIsSubmitting(false);
      return;
    }
    
    const originalThumbnailUrl = finalThumbnailUrl;
    const needsMigration = isExternalUrl(finalThumbnailUrl);

    try {
      // Validar duplicados antes de submeter (agora é assíncrono)
      const isValid = await validateDuplicates();
      if (!isValid) {
        submittingRef.current = false;
        setIsSubmitting(false);
        return;
      }
      // Processar o código: se for um JSON válido do Elementor, fazer parse para objeto
      // Caso contrário, manter como string
      let processedCode: string | any = formData.code.trim();
      
      try {
        // Tentar fazer parse do JSON
        const parsedCode = JSON.parse(processedCode);
        
        // Se for um objeto e tiver a estrutura do Elementor, usar o objeto parseado
        if (parsedCode && typeof parsedCode === 'object' && parsedCode.type === 'elementor') {
          processedCode = parsedCode; // Salvar como objeto, não como string
          console.log('✅ Código Elementor detectado, salvando como objeto JSONB');
        }
        // Se não for Elementor mas for JSON válido, manter como objeto também
        else if (parsedCode && typeof parsedCode === 'object') {
          processedCode = parsedCode;
          console.log('✅ JSON válido detectado, salvando como objeto JSONB');
        }
        // Se não for objeto, manter como string original
      } catch (e) {
        // Não é JSON válido, manter como string
        console.log('ℹ️ Código não é JSON válido, salvando como string');
      }

      // Preparar o objeto data baseado no tipo de código
      let cardData: any = {
        description: formData.description.trim()
      };

      if (formData.code_type === 'json') {
        // Para JSON, usar campo único code
        cardData.code = processedCode;
      } else {
        // Para HTML/CSS/JS, usar campos separados
        if (formData.html_code.trim()) {
          cardData.html_code = formData.html_code.trim();
        }
        if (formData.css_code.trim()) {
          cardData.css_code = formData.css_code.trim();
        }
        if (formData.js_code.trim()) {
          cardData.js_code = formData.js_code.trim();
        }
        if (formData.html_instructions.trim()) {
          cardData.html_instructions = formData.html_instructions.trim();
        }
        if (formData.css_instructions.trim()) {
          cardData.css_instructions = formData.css_instructions.trim();
        }
        if (formData.js_instructions.trim()) {
          cardData.js_instructions = formData.js_instructions.trim();
        }
      }

      // Criar o card no banco primeiro para obter o ID
      const { data: insertedData, error } = await supabase
        .from('cards')
        .insert([{
          user_id: user.id,
          title: formData.title.trim(),
          thumbnail: finalThumbnailUrl, // URL temporária (será atualizada se migração for bem-sucedida)
          data: cardData,
          category: formData.categories.length > 0 ? formData.categories[0] : 'Geral',
          categories: formData.categories.length > 0 ? formData.categories : ['Geral'],
          code_type: formData.code_type,
          is_editable: true
        }])
        .select()
        .single();

      if (error) {
        console.error('Erro ao salvar:', error);
        alert('Erro ao salvar o elemento. Tente novamente.');
        submittingRef.current = false;
        setIsSubmitting(false);
        setIsUploadingThumbnail(false);
        return;
      }

      // Rastrear criação de card para gamificação (assíncrono, não bloqueante)
      if (user?.id) {
        incrementCardsCreatedCount(user.id).catch(err => {
          console.error('Erro ao rastrear criação de card:', err);
          // Erro silencioso - não afetar UX
        });
      }

      // Nota: Upload automático de vídeos externos via Edge Function foi desabilitado
      // pois requer autenticação no PackLP que não está funcionando.
      // Use o upload direto de arquivo ou aguarde migração automática.
      const isVideo = finalThumbnailUrl.toLowerCase().includes('.mp4');
      if (needsMigration && insertedData?.id) {
        if (isVideo) {
          console.log('📝 Card criado com URL de vídeo externa.');
          console.log('   Nota: Para vídeos do PackLP, use o upload direto de arquivo.');
          console.log('   A URL externa foi salva e pode ser migrada manualmente depois.');
        } else {
          console.log('📝 Card criado com URL externa. A migração será feita automaticamente pelo cron job.');
        }
        // A URL externa já está salva no banco
      }

      // Navegar para dashboard - os dados serão carregados lá se necessário
      navigate('/dashboard', { state: { refresh: true } });
    } catch (error) {
      console.error('Erro inesperado:', error);
      alert('Erro inesperado. Tente novamente.');
      submittingRef.current = false;
      setIsSubmitting(false);
      setIsUploadingThumbnail(false);
    } finally {
      // Garantir que sempre reseta o estado, mesmo se houver erro
      submittingRef.current = false;
      setIsSubmitting(false);
      setIsUploadingThumbnail(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gradient-to-br dark:from-[#1a0f2b] dark:via-[#2d1b3d] dark:to-[#1a0f2b] transition-colors duration-300">
      {/* Header */}
      <header className="bg-white dark:bg-gradient-to-r dark:from-[#1a0f2b] dark:to-[#0d0d0d] shadow-lg border-b border-gray-200 dark:border-[#8a2be2]/30">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 w-full">
            {/* Lado Esquerdo - Título */}
            <div className="flex items-center space-x-3">
              <button
                onClick={() => navigate('/dashboard')}
                className="p-2 text-gray-600 dark:text-[#c780ff] hover:text-gray-900 dark:hover:text-[#e0e1dd] rounded-lg hover:bg-gray-100 dark:hover:bg-[#6a0dad]/30 transition-colors duration-200"
              >
                <i className="ri-arrow-left-line text-xl"></i>
              </button>
              <div>
                <h1 className="text-xl font-bold text-gray-900 dark:text-[#e0e1dd] font-ubuntu">Cadastrar Novo Card</h1>
                <p className="text-sm text-gray-600 dark:text-[#c780ff] font-montserrat">Adicione um novo código ao seu arsenal</p>
              </div>
            </div>
            
            {/* Lado Direito - Ações */}
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="hidden sm:flex items-center space-x-2 px-4 py-2 text-gray-600 dark:text-[#c780ff] hover:text-gray-900 dark:hover:text-[#e0e1dd] rounded-lg hover:bg-gray-100 dark:hover:bg-[#6a0dad]/30 transition-colors duration-200 font-montserrat whitespace-nowrap"
              >
                <i className="ri-arrow-left-line"></i>
                <span>Voltar ao Dashboard</span>
              </button>
              <ThemeToggle />
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white dark:bg-gradient-to-br dark:from-[#1a0f2b] dark:to-[#2d1b3d] rounded-xl shadow-lg border border-gray-200 dark:border-[#8a2be2]/30 relative">
          {/* Botão Fechar */}
          <button
            onClick={() => navigate('/dashboard')}
            className="absolute top-2 right-4 p-2 text-gray-500 dark:text-[#c780ff] hover:text-gray-700 dark:hover:text-[#e0e1dd] hover:bg-gray-100 dark:hover:bg-[#0d0d0d]/50 rounded-lg transition-colors duration-200 z-10"
            aria-label="Fechar e voltar ao dashboard"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
          
          <div className="p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Título do Card */}
              <div>
                <label htmlFor="title" className="block text-sm font-medium text-gray-700 dark:text-[#e0e1dd] mb-2 font-montserrat">
                  Título do Card
                </label>
                <input
                  type="text"
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-[#8a2be2]/30 rounded-lg focus:ring-2 focus:ring-[#8a2be2] focus:border-[#8a2be2] dark:bg-[#0d0d0d]/50 dark:text-[#e0e1dd] transition-colors duration-200 font-montserrat"
                  placeholder="Digite o título do card"
                  required
                />
              </div>

              {/* Tipo de Código */}
              <div>
                <label htmlFor="code_type" className="block text-sm font-medium text-gray-700 dark:text-[#e0e1dd] mb-2 font-montserrat">
                  Tipo de Código
                </label>
                <div className="relative">
                  <select
                    id="code_type"
                    value={formData.code_type}
                    onChange={(e) => setFormData({...formData, code_type: e.target.value})}
                    className="w-full px-4 py-3 pr-14 border border-gray-300 dark:border-[#8a2be2]/30 rounded-lg focus:ring-2 focus:ring-[#8a2be2] focus:border-[#8a2be2] dark:bg-[#0d0d0d]/50 dark:text-[#e0e1dd] transition-colors duration-200 font-montserrat appearance-none"
                    required
                  >
                    {codeTypes.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                  <div className="absolute right-6 top-1/2 transform -translate-y-1/2 pointer-events-none">
                    <i className="ri-arrow-down-s-line text-xl text-gray-500 dark:text-[#c780ff]"></i>
                  </div>
                </div>
              </div>

              {/* Categorias */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-[#e0e1dd] mb-3 font-montserrat">
                  Categorias (selecione uma ou mais)
                </label>
                
                {loadingCategories ? (
                  <div className="flex items-center justify-center py-8">
                    <i className="ri-loader-4-line text-2xl text-[#6a0dad] animate-spin"></i>
                    <span className="ml-2 text-gray-600 dark:text-[#c780ff]">Carregando categorias...</span>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {availableCategories.map((category) => (
                      <label key={category} className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.categories.includes(category)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFormData({
                                ...formData, 
                                categories: [...formData.categories, category],
                                category: [...formData.categories, category].join(',')
                              });
                            } else {
                              const newCategories = formData.categories.filter(c => c !== category);
                              setFormData({
                                ...formData, 
                                categories: newCategories,
                                category: newCategories.join(',')
                              });
                            }
                          }}
                          className="w-4 h-4 text-[#6a0dad] bg-gray-100 border-gray-300 rounded focus:ring-[#8a2be2] dark:focus:ring-[#8a2be2] dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                        />
                        <span className="text-sm text-gray-700 dark:text-[#e0e1dd] font-montserrat">{category}</span>
                      </label>
                    ))}
                  </div>
                )}
                
                {/* Botão Adicionar Nova Categoria */}
                <div className="mt-4">
                  <Button
                    type="button"
                    onClick={() => setShowNewCategoryModal(true)}
                    type="secondary"
                    size="medium"
                    className="border-2 border-dashed border-[#6a0dad] dark:border-[#8a2be2] text-[#6a0dad] dark:text-[#8a2be2] hover:bg-[#6a0dad]/10 dark:hover:bg-[#8a2be2]/10"
                    prefix={<i className="ri-add-line"></i>}
                  >
                    Adicionar Nova Categoria
                  </Button>
                </div>
                
                {formData.categories.length > 0 && (
                  <div className="mt-3">
                    <p className="text-sm text-gray-600 dark:text-[#c780ff] font-montserrat">
                      Selecionadas: <span className="font-medium">{formData.categories.join(', ')}</span>
                    </p>
                  </div>
                )}
              </div>

              {/* URL da Miniatura ou Colar Imagem */}
              <div>
                <label htmlFor="thumbnail" className="block text-sm font-medium text-gray-700 dark:text-[#e0e1dd] mb-2 font-montserrat">
                  URL da Miniatura ou Cole uma Imagem (Ctrl+V)
                </label>
                <div className="space-y-3">
                  <input
                    type="url"
                    id="thumbnail"
                    value={formData.thumbnail}
                    onChange={(e) => setFormData({...formData, thumbnail: e.target.value})}
                    onPaste={async (e) => {
                      // Detectar se o usuário colou uma imagem
                      const items = e.clipboardData?.items;
                      if (!items) return;

                      for (let i = 0; i < items.length; i++) {
                        const item = items[i];
                        
                        // Verificar se é uma imagem
                        if (item.type.indexOf('image') !== -1) {
                          e.preventDefault();
                          
                          const file = item.getAsFile();
                          if (!file) continue;

                          setIsUploadingThumbnail(true);
                          setThumbnailLoading(true);
                          setThumbnailError(null);
                          
                          try {
                            // Criar blob URL temporário para preview imediato
                            const blobUrl = URL.createObjectURL(file);
                            setFormData({...formData, thumbnail: blobUrl});
                            setThumbnailLoading(false);
                            
                            // Detectar tipo MIME e extensão
                            // IMPORTANTE: Garantir que o tipo MIME seja sempre uma imagem
                            let mimeType = file.type;
                            if (!mimeType || !mimeType.startsWith('image/')) {
                              // Se o tipo não for detectado ou não for imagem, usar baseado na extensão
                              const fileName = file.name || 'image.png';
                              if (fileName.endsWith('.png')) {
                                mimeType = 'image/png';
                              } else if (fileName.endsWith('.jpg') || fileName.endsWith('.jpeg')) {
                                mimeType = 'image/jpeg';
                              } else if (fileName.endsWith('.gif')) {
                                mimeType = 'image/gif';
                              } else if (fileName.endsWith('.webp')) {
                                mimeType = 'image/webp';
                              } else {
                                mimeType = 'image/png'; // Padrão seguro
                              }
                            }
                            
                            const ext = mimeType.split('/')[1] || 'png';
                            const fileName = `temp/${Date.now()}_${Math.random().toString(36).substring(7)}.${ext}`;
                            
                            console.log('📤 Fazendo upload:');
                            console.log('   Arquivo original type:', file.type);
                            console.log('   Tipo MIME usado:', mimeType);
                            console.log('   Nome do arquivo:', fileName);
                            
                            // IMPORTANTE: Criar um novo Blob com o tipo MIME correto
                            // Ler o arquivo como ArrayBuffer e criar um novo Blob com o tipo correto
                            const arrayBuffer = await file.arrayBuffer();
                            const blob = new Blob([arrayBuffer], { type: mimeType });
                            
                            console.log('   Blob criado com type:', blob.type);
                            
                            // Fazer upload para Supabase Storage usando API REST diretamente
                            // Isso garante que o Content-Type seja respeitado
                            const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL || 'https://bwrjzklcyhddlpzbjlgd.supabase.co';
                            const supabaseKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY || '';
                            const { data: { session } } = await supabase.auth.getSession();
                            const token = session?.access_token || supabaseKey;
                            
                            const uploadUrl = `${supabaseUrl}/storage/v1/object/thumbnails/${fileName}`;
                            
                            console.log('📤 Fazendo upload via API REST:', uploadUrl);
                            
                            const uploadResponse = await fetch(uploadUrl, {
                              method: 'POST',
                              headers: {
                                'Authorization': `Bearer ${token}`,
                                'Content-Type': mimeType,
                                'x-upsert': 'true',
                                'Cache-Control': '3600',
                                'apikey': supabaseKey
                              },
                              body: arrayBuffer
                            });
                            
                            if (!uploadResponse.ok) {
                              const errorText = await uploadResponse.text();
                              throw new Error(`Upload falhou: ${uploadResponse.status} - ${errorText}`);
                            }
                            
                            const uploadData = await uploadResponse.json();
                            console.log('✅ Upload bem-sucedido via API REST');
                            console.log('   Path:', uploadData.path || fileName);

                            // Aguardar um pouco para garantir que o arquivo está disponível
                            await new Promise(resolve => setTimeout(resolve, 500));

                            // Obter URL pública do Supabase
                            // IMPORTANTE: getPublicUrl retorna a URL correta para buckets públicos
                            const { data: { publicUrl } } = supabase.storage
                              .from('thumbnails')
                              .getPublicUrl(fileName);

                            // Remover qualquer query param que possa estar na URL
                            const cleanUrl = publicUrl.split('?')[0];

                            console.log('✅ Upload concluído');
                            console.log('   Arquivo:', fileName);
                            console.log('   URL do Supabase (limpa):', cleanUrl);
                            
                            // SEMPRE substituir blob URL pela URL do Supabase após upload bem-sucedido
                            // O blob URL não funciona após recarregar a página ou em outros dispositivos
                            URL.revokeObjectURL(blobUrl);
                            
                            // Usar URL limpa do Supabase (sem query params)
                            // Buckets públicos não precisam de autenticação para leitura
                            setFormData({...formData, thumbnail: cleanUrl});
                            console.log('✅ Blob URL substituído pela URL do Supabase (limpa)');
                            
                            // Verificar se a URL está correta (deve começar com a URL do projeto)
                            if (!cleanUrl.startsWith(supabaseUrl)) {
                              console.error('❌ URL gerada não corresponde ao projeto Supabase!');
                              console.error('   URL gerada:', cleanUrl);
                              console.error('   URL esperada:', supabaseUrl);
                            } else {
                              console.log('✅ URL verificada e corresponde ao projeto');
                            }
                          } catch (error: any) {
                            console.error('Erro ao fazer upload da imagem colada:', error);
                            setThumbnailError('Erro ao fazer upload da imagem: ' + (error.message || 'Erro desconhecido'));
                            setThumbnailLoading(false);
                            alert('Erro ao fazer upload da imagem: ' + (error.message || 'Erro desconhecido'));
                          } finally {
                            setIsUploadingThumbnail(false);
                          }
                          break;
                        }
                      }
                    }}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-[#8a2be2]/30 rounded-lg focus:ring-2 focus:ring-[#8a2be2] focus:border-[#8a2be2] dark:bg-[#0d0d0d]/50 dark:text-[#e0e1dd] transition-colors duration-200 font-montserrat"
                    placeholder="Cole uma imagem (Ctrl+V) ou cole uma URL aqui"
                  />
                  <div className="text-xs text-gray-500 dark:text-[#778da9] flex items-center space-x-1">
                    <i className="ri-information-line"></i>
                    <span>Dica: Você pode colar uma imagem diretamente (Ctrl+V) ou colar uma URL de imagem</span>
                  </div>
                  {isUploadingThumbnail && (
                    <div className="flex items-center space-x-2 text-sm text-[#6a0dad] dark:text-[#c780ff]">
                      <i className="ri-loader-4-line animate-spin"></i>
                      <span>Enviando imagem...</span>
                    </div>
                  )}
                </div>
                {formData.thumbnail && (
                  <div className="mt-3">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm text-gray-600 dark:text-[#c780ff]">Preview da miniatura:</p>
                      {isExternalUrl(formData.thumbnail) && (
                        <span className="text-xs text-blue-600 dark:text-blue-400 flex items-center space-x-1">
                          <i className="ri-upload-cloud-line"></i>
                          <span>Será migrada automaticamente para o Supabase</span>
                        </span>
                      )}
                    </div>
                    <div className="relative w-32 h-20 bg-gray-100 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-[#8a2be2]/30 overflow-hidden">
                      {thumbnailLoading && (
                        <div className="absolute inset-0 flex items-center justify-center bg-gray-100 dark:bg-gray-800">
                          <i className="ri-loader-4-line animate-spin text-[#6a0dad] text-xl"></i>
                        </div>
                      )}
                      {thumbnailError && (
                        <div className="absolute inset-0 flex items-center justify-center bg-gray-100 dark:bg-gray-800 p-2">
                          <p className="text-xs text-red-500 text-center">{thumbnailError}</p>
                        </div>
                      )}
                      <img 
                        key={formData.thumbnail}
                        src={formData.thumbnail} 
                        alt="Preview" 
                        className={`w-full h-full object-cover ${thumbnailError ? 'hidden' : ''}`}
                        onLoad={() => {
                          console.log('✅ Imagem carregada com sucesso:', formData.thumbnail);
                          setThumbnailLoading(false);
                          setThumbnailError(null);
                        }}
                        onError={(e) => {
                          const target = e.currentTarget;
                          const currentUrl = formData.thumbnail;
                          
                          // Se for um blob URL, não fazer nada (já está funcionando)
                          if (currentUrl.startsWith('blob:')) {
                            return;
                          }
                          
                          console.error('❌ Erro ao carregar imagem do Supabase:', currentUrl);
                          console.error('   Natural width:', target.naturalWidth);
                          console.error('   Natural height:', target.naturalHeight);
                          console.error('   Complete:', target.complete);
                          
                          // Tentar remover query params se houver (pode estar causando problemas)
                          const urlWithoutParams = currentUrl.split('?')[0];
                          if (urlWithoutParams !== currentUrl) {
                            console.log('🔄 Tentando carregar sem query params:', urlWithoutParams);
                            setThumbnailLoading(true);
                            setTimeout(() => {
                              setFormData({...formData, thumbnail: urlWithoutParams});
                            }, 500);
                          } else {
                            // Se chegou aqui, a URL do Supabase não está acessível
                            setThumbnailLoading(false);
                            setThumbnailError('A imagem foi enviada, mas não está carregando. Isso pode ser um problema de CORS ou o arquivo ainda não está disponível. Tente recarregar a página.');
                          }
                        }}
                      />
                    </div>
                    {thumbnailError && (
                      <p className="mt-2 text-xs text-red-500">
                        {thumbnailError}
                        {formData.thumbnail.toLowerCase().includes('.mp4') && (
                          <span className="block mt-1">Nota: Vídeos podem não carregar devido a restrições de CORS.</span>
                        )}
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Código - JSON ou HTML/CSS/JS separados */}
              {formData.code_type === 'json' ? (
                <div>
                  <label htmlFor="code" className="flex items-center space-x-2 text-sm font-medium text-gray-700 dark:text-[#e0e1dd] mb-2 font-montserrat">
                    <i className="ri-braces-line"></i>
                    <span>Código JSON</span>
                  </label>
                  <textarea
                    id="code"
                    value={formData.code}
                    onChange={(e) => setFormData({...formData, code: e.target.value})}
                    rows={15}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-[#8a2be2]/30 rounded-lg focus:ring-2 focus:ring-[#8a2be2] focus:border-[#8a2be2] dark:bg-[#0d0d0d]/50 dark:text-[#e0e1dd] font-mono text-sm transition-colors duration-200"
                    placeholder="Digite um código json válido"
                    required
                  />
                  <p className="mt-2 text-xs text-gray-500 dark:text-[#778da9] font-montserrat">
                    Digite um código json válido
                  </p>
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-[#e0e1dd] mb-3 font-montserrat">
                    Códigos (HTML, CSS, JavaScript)
                  </label>
                  <CodeAccordion
                    htmlCode={formData.html_code}
                    cssCode={formData.css_code}
                    jsCode={formData.js_code}
                    htmlInstructions={formData.html_instructions}
                    cssInstructions={formData.css_instructions}
                    jsInstructions={formData.js_instructions}
                    onHtmlCodeChange={(value) => setFormData({...formData, html_code: value})}
                    onCssCodeChange={(value) => setFormData({...formData, css_code: value})}
                    onJsCodeChange={(value) => setFormData({...formData, js_code: value})}
                    onHtmlInstructionsChange={(value) => setFormData({...formData, html_instructions: value})}
                    onCssInstructionsChange={(value) => setFormData({...formData, css_instructions: value})}
                    onJsInstructionsChange={(value) => setFormData({...formData, js_instructions: value})}
                  />
                  <p className="mt-3 text-xs text-gray-500 dark:text-[#778da9] font-montserrat">
                    <i className="ri-information-line mr-1"></i>
                    Pelo menos um dos códigos (HTML, CSS ou JS) deve estar preenchido. Os campos vazios serão exibidos como "Opcional" na visualização.
                  </p>
                </div>
              )}

              {/* Botão de Cadastro */}
              <div className="flex justify-center pt-6">
                <Button
                  buttonType="submit"
                  disabled={isSubmitting || isUploadingThumbnail}
                  loading={isSubmitting || isUploadingThumbnail}
                  type="primary"
                  size="large"
                  className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 shadow-lg hover:shadow-xl"
                  prefix={!isSubmitting && !isUploadingThumbnail ? <i className="ri-save-line"></i> : undefined}
                >
                  {isUploadingThumbnail ? 'Migrando thumbnail...' : isSubmitting ? 'Cadastrando...' : 'Cadastrar Card'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </main>

      {/* Modal Nova Categoria */}
      {showNewCategoryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gradient-to-br dark:from-[#1a0f2b] dark:to-[#2d1b3d] rounded-xl shadow-2xl max-w-md w-full border border-gray-200 dark:border-[#8a2be2]/30">
            <div className="p-6 border-b border-gray-200 dark:border-[#8a2be2]/30">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900 dark:text-[#e0e1dd] font-ubuntu">
                  Nova Categoria
                </h2>
                <IconButton
                  onClick={() => {
                    setShowNewCategoryModal(false);
                    setNewCategoryName('');
                  }}
                  variant="ghost"
                  size="md"
                >
                  <i className="ri-close-line text-xl"></i>
                </IconButton>
              </div>
            </div>
            
            <div className="p-6">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-[#e0e1dd] mb-2 font-montserrat">
                  Nome da Categoria
                </label>
                <input
                  type="text"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-[#8a2be2]/30 rounded-lg focus:ring-2 focus:ring-[#8a2be2] focus:border-[#8a2be2] dark:bg-[#0d0d0d]/50 dark:text-[#e0e1dd] transition-colors duration-200 font-montserrat"
                  placeholder="Ex: Animações, Layouts, etc."
                  autoFocus
                />
              </div>
              
              <div className="flex justify-end space-x-3">
                <Button
                  onClick={() => {
                    setShowNewCategoryModal(false);
                    setNewCategoryName('');
                  }}
                  type="secondary"
                  size="medium"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleAddCategory}
                  type="primary"
                  size="medium"
                >
                  Adicionar
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
